import React, { useMemo } from "react";
import { Player } from "@remotion/player";
import { Alert, Button, Modal, Spin, Typography } from "antd";

import {
  CLIP_FPS,
  ClipPreview,
  captionAnchorForFraming,
  DEFAULT_CLIP_BRAND,
  planClipCaptionsForRanges,
  toCaptionedClipStyle,
  toCaptionPagingOptions,
  type ClipCaptionPlan,
} from "@fitness-app/ai/studio-editor";
import { StudioClipStatus } from "@fitness-app/data-models/entities/StudioClip";

import { useRenderClips } from "../hooks/useStudioAds";
import { useStudioClipEditorData } from "../hooks/useClipEditor";
import { type StudioClipRow } from "../hooks/useStudioData";

const PREVIEW_WIDTH = 324;
const PREVIEW_HEIGHT = 576;

const ROLE_LABELS: Record<string, string> = { hook: "Hook", value: "Value", cta: "CTA" };

const formatMs = (ms: number) => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
};

/**
 * Podgląd wariantu reklamy (etap G) bez renderu: hook → value → CTA sklejone wprost z
 * nagrania źródłowego tą samą logiką (snap do zdań, trim ciszy, strony napisów), której
 * użyje render. Render uruchamia się dopiero przyciskiem, bo każdy kosztuje.
 */
export const VariantPreviewModal = ({
  clip,
  onClose,
}: {
  clip: StudioClipRow;
  onClose: () => void;
}): React.ReactElement => {
  const editorQuery = useStudioClipEditorData(clip);
  const renderClips = useRenderClips();

  const plan = useMemo<{ plan: ClipCaptionPlan | null; error: string | null }>(() => {
    if (!editorQuery.data) return { plan: null, error: null };
    try {
      return {
        plan: planClipCaptionsForRanges(
          editorQuery.data.words,
          editorQuery.data.ranges,
          toCaptionPagingOptions(DEFAULT_CLIP_BRAND),
        ),
        error: null,
      };
    } catch {
      return { plan: null, error: "Któraś część nie zawiera pełnego zdania. Wybierz inne segmenty." };
    }
  }, [editorQuery.data]);

  const durationMs = plan.plan?.durationMs ?? 0;
  const canRender = clip.status === StudioClipStatus.Candidate || clip.status === StudioClipStatus.RenderFailed;

  return (
    <Modal
      open
      width="min(760px, 96vw)"
      title={`Podgląd reklamy: ${clip.hook.slice(0, 70)}`}
      onCancel={onClose}
      footer={
        <div className="flex items-center justify-between">
          <Typography.Text type="secondary">Długość: {formatMs(durationMs)}</Typography.Text>
          <div className="flex gap-2">
            <Button onClick={onClose}>Zamknij</Button>
            {canRender && (
              <Button
                type="primary"
                loading={renderClips.isLoading}
                disabled={!plan.plan}
                onClick={() => renderClips.mutate([clip.id], { onSuccess: onClose })}
              >
                Renderuj ten wariant
              </Button>
            )}
          </div>
        </div>
      }
    >
      {editorQuery.isLoading && (
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      )}
      {editorQuery.isError && (
        <Alert type="error" showIcon message="Nie udało się wczytać transkrypcji tego nagrania." />
      )}
      {editorQuery.data && (
        <div className="flex gap-6">
          <div className="shrink-0" style={{ width: PREVIEW_WIDTH }}>
            {plan.plan ? (
              <Player
                component={ClipPreview}
                inputProps={{
                  src: editorQuery.data.sourceUrl,
                  segments: plan.plan.segments,
                  events: plan.plan.events,
                  style: toCaptionedClipStyle(
                    DEFAULT_CLIP_BRAND,
                    captionAnchorForFraming(clip.renderMeta?.framing?.mode),
                  ),
                  framing: clip.renderMeta?.framing ?? null,
                  logoUrl: DEFAULT_CLIP_BRAND.logoUrl,
                }}
                durationInFrames={Math.max(1, Math.ceil((durationMs / 1000) * CLIP_FPS))}
                fps={CLIP_FPS}
                compositionWidth={DEFAULT_CLIP_BRAND.targetWidth}
                compositionHeight={DEFAULT_CLIP_BRAND.targetHeight}
                style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT, borderRadius: 8, overflow: "hidden" }}
                controls
                loop
              />
            ) : (
              <div
                className="flex items-center justify-center rounded bg-gray-100 p-4 text-center text-sm text-gray-500"
                style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
              >
                {plan.error ?? "Brak podglądu"}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <Typography.Paragraph type="secondary" className="!mb-3 text-xs">
              Podgląd gra z nagrania źródłowego, z pełnym obrazem. Kadr na twarz i napisy w wersji finalnej dostaniesz
              po renderze.
            </Typography.Paragraph>
            {(clip.parts ?? []).map((part, index) => (
              <div key={`${part.segmentId}-${index}`} className="mb-3 rounded bg-gray-50 px-3 py-2">
                <Typography.Text strong>
                  {ROLE_LABELS[part.role] ?? part.role} · {formatMs(part.startMs)}–{formatMs(part.endMs)}
                </Typography.Text>
              </div>
            ))}
            <Typography.Paragraph className="!mb-0 whitespace-pre-line text-xs text-gray-600">
              {clip.reasoning}
            </Typography.Paragraph>
          </div>
        </div>
      )}
    </Modal>
  );
};
